import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Switch,
    TextField,
    Alert,
    AlertTitle,
    Button,
    CircularProgress
} from '@mui/material';
import HtmlEditor from '../../../Common/HtmlEditor';
import type { PlaceholderItem } from '../../../Common/HtmlEditor';
import communicationService from '../../../../services/CommunicationService';
import { CommunicationTemplate, CommunicationEventType, CommunicationTemplateUpdate } from '../../../../types/Communication';
import {
    ExpandMore as ExpandMoreIcon,
    Email as EmailIcon,
    Handshake as HandshakeIcon,
    RateReview as RateReviewIcon,
    RepeatOne as RepeatOneIcon,
    SyncAlt as SyncAltIcon,
    Block as BlockIcon,
    Help as HelpIcon
} from '@mui/icons-material';

interface CommunicationTemplatesProps {
    campaignId: string;
    organizationId: string;
}

const PLACEHOLDERS: PlaceholderItem[] = [
    { token: 'USER_FIRST_NAME', label: 'User first name' },
    { token: 'MENTOR_NAME', label: 'Mentor name' },
    { token: 'ORGANIZATION_NAME', label: 'Organization name' },
    { token: 'CAMPAIGN_NAME', label: 'Campaign name' },
    { token: 'FEEDBACK_LINK', label: 'Feedback form link' }
];

const CommunicationTemplatesPanel: React.FC<CommunicationTemplatesProps> = ({
    campaignId,
    organizationId
}) => {
    // Local template management
    const [templates, setTemplates] = useState<CommunicationTemplate[]>([]);
    const [expanded, setExpanded] = useState<string | false>(false);
    const [loading, setLoading] = useState<boolean>(true)

    // Fetch templates on mount
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const apiTemplates = await communicationService.listTemplates(organizationId, campaignId);
                setTemplates(apiTemplates);
                // Expand the first template panel by default
                if (apiTemplates.length > 0) {
                    setExpanded(`template-${apiTemplates[0].id}`);
                }
            } catch (err) {
                console.error('Failed to load templates', err);
            } finally {
                setLoading(false);
            }
        };
        fetchTemplates();
    }, [organizationId, campaignId]);

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleTemplateChange = (templateId: string, field: keyof CommunicationTemplate, value: any) => {
        setTemplates(templates.map(template =>
            template.id === templateId ? { ...template, [field]: value } : template
        ));
    };

    const handleSaveTemplates = () => {
        setLoading(true);
        Promise.all(
            templates.map((t) => {
                const update: CommunicationTemplateUpdate = {
                    subject: t.subject,
                    body: t.body,
                    isEnabled: t.isEnabled
                };
                return communicationService.updateTemplate(organizationId, campaignId, t.id, update);
            })
        )
            .then(() => {
                console.log('Templates saved');
            })
            .catch((err) => console.error('Failed to save templates', err))
            .finally(() => setLoading(false));
    };


    const getEventTitle = (eventType: CommunicationEventType): string => {
        switch (eventType) {
            case 'USER_SUBMITS_APPLICATION':
                return 'Applicant Submits Application';
            case 'USER_IS_MATCHED':
                return 'Applicant is matched';
            case 'USER_IS_REMATCHED':
                return 'Applicant is rematched';
            case 'USER_IS_NOT_MATCHED':
                return 'Applicant is not matched';
            case 'FEEDBACK_IS_REQUESTED':
                return 'Feedback is requested';
            case 'WEEKLY_CHECK_IN':
                return 'Weekly check-in';
            default:
                return eventType;
        }
    };

    const getTriggerIcon = (eventType: CommunicationEventType) => {
        switch (eventType) {
            case 'USER_SUBMITS_APPLICATION':
                return <EmailIcon />;
            case 'USER_IS_MATCHED':
                return <HandshakeIcon />;
            case 'FEEDBACK_IS_REQUESTED':
                return <RateReviewIcon />;
            case 'WEEKLY_CHECK_IN':
                return <RepeatOneIcon />;
            case 'USER_IS_REMATCHED':
                return <SyncAltIcon />;
            case 'USER_IS_NOT_MATCHED':
                return <BlockIcon />;
            default:
                return <HelpIcon />;
        }
    };

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h5">Communication Templates</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveTemplates}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {loading ? 'Saving…' : 'Save Templates'}
                    </Button>
                </Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                    <AlertTitle>About Communication Templates</AlertTitle>
                    Configure email templates that will be automatically sent to participants at key moments in your mentorship program.
                    Enable or disable templates, customize content, and set sending frequency as needed.
                </Alert>
            </Box>

            <Box sx={{ mb: 3 }}>
                {templates.map((template) => (
                    <Accordion
                        key={template.id}
                        expanded={expanded === `template-${template.id}`}
                        onChange={handleAccordionChange(`template-${template.id}`)}
                        sx={{ mb: 2, borderRadius: 1, overflow: 'hidden' }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                                        {getTriggerIcon(template.eventType)}
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>{getEventTitle(template.eventType)}</Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={template.isEnabled}
                                            onChange={(e) => handleTemplateChange(template.id, 'isEnabled', e.target.checked)}
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.stopPropagation()}
                                        />
                                    }
                                    label={template.isEnabled ? "Enabled" : "Disabled"}
                                    sx={{ mr: 0 }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ my: 2, borderBottom: '1px solid', borderColor: 'divider' }} />

                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Email Subject"
                                    value={template.subject}
                                    onChange={(e) => handleTemplateChange(template.id, 'subject', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    sx={{ mb: 2 }}
                                />

                                <HtmlEditor
                                    placeholders={PLACEHOLDERS}
                                    value={template.body}
                                    onChange={(val) => handleTemplateChange(template.id, 'body', val)}
                                    minRows={6}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </Box>
    );
};

export default CommunicationTemplatesPanel;